import { cn } from "@/lib/utils";
import { Check, ChevronsUpDown, X } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDropdownPlacement } from "./use-dropdown-placement";

export type MultiSelectOption = {
  value: string;
  label: string;
  /** Optional grouping label rendered as a non-interactive header
   *  before the first item with that group (e.g. "Hub users" vs
   *  "Clockify-only"). Items are rendered in the order received. */
  group?: string;
};

type Props = {
  /** All options available to pick. Order is respected. */
  options: MultiSelectOption[];
  /** Currently chosen values. */
  values: string[];
  /** Negation mode — when true the filter excludes the chosen
   *  values instead of including them. */
  exclude: boolean;
  onChange: (values: string[], exclude: boolean) => void;
  /** Button label when nothing is chosen. */
  placeholder: string;
  /** Optional summary noun for the chip on the button when ≥2 are
   *  chosen, e.g. "users" / "projects" / "tags". */
  noun?: string;
  /** When set, hides the entire control (callers use this for tag
   *  filters in workspaces with no tags). */
  hidden?: boolean;
  "aria-label"?: string;
};

/**
 * Combined "include / exclude / any-of" filter.
 *
 * Click the button → a popover lists every option with a checkbox.
 * Above the list a tiny mode toggle flips include ↔ exclude. The
 * value string is whichever options are currently checked; the
 * caller is responsible for serializing them into the URL via
 * `serializeListFilter` so the grammar stays in sync with the
 * server's parser (`server/lib/list-filter.ts`).
 *
 * Pattern mirrors `combobox.tsx`: click-outside / Esc to close,
 * search box to filter long lists, no Radix Popover dependency
 * (the project ships only DropdownMenu + Select from Radix).
 */
export function MultiSelectFilter({
  options,
  values,
  exclude,
  onChange,
  placeholder,
  noun,
  hidden,
  "aria-label": ariaLabel,
}: Props) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  // Mode toggle + search + list max-h-64 (16rem) + footer ≈ 21rem.
  const placement = useDropdownPlacement(wrapperRef, open, 336);

  const selected = useMemo(() => new Set(values), [values]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => o.label.toLowerCase().includes(q));
  }, [options, query]);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => inputRef.current?.focus(), 0);
    return () => clearTimeout(t);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      if (!wrapperRef.current?.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  if (hidden) return null;

  function toggle(value: string) {
    if (selected.has(value)) {
      onChange(
        values.filter((v) => v !== value),
        exclude,
      );
    } else {
      onChange([...values, value], exclude);
    }
  }

  function clearAll() {
    onChange([], false);
    setOpen(false);
  }

  // Button label: "All <noun>" when nothing is chosen, "Not <label>"
  // for a single-value exclude, "<label>" for a single include,
  // "<N> <noun>" (with "not" prefix in exclude mode) when ≥2.
  const label = (() => {
    if (values.length === 0) return placeholder;
    if (values.length === 1) {
      const opt = options.find((o) => o.value === values[0]);
      const text = opt?.label ?? values[0];
      return exclude ? `Not: ${text}` : text;
    }
    const summary = `${values.length} ${noun ?? "items"}`;
    return exclude ? `Not: ${summary}` : `Any: ${summary}`;
  })();

  return (
    <div ref={wrapperRef} className="relative">
      <button
        type="button"
        aria-label={ariaLabel ?? placeholder}
        onClick={() => setOpen((o) => !o)}
        className={cn(
          "inline-flex h-9 items-center gap-2 rounded-md border border-input bg-background px-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring",
          values.length > 0 && "border-primary text-primary",
        )}
      >
        <span className="truncate max-w-48">{label}</span>
        {values.length > 0 ? (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              clearAll();
            }}
            aria-label="Clear filter"
            className="rounded p-0.5 text-muted-foreground hover:bg-accent hover:text-foreground"
          >
            <X className="h-3 w-3" />
          </button>
        ) : (
          <ChevronsUpDown className="h-3 w-3 text-muted-foreground" />
        )}
      </button>

      {open && (
        <div
          className={cn(
            "absolute z-50 w-72 rounded-md border border-input bg-popover p-2 shadow-md",
            placement === "up" ? "bottom-full mb-1" : "top-full mt-1",
          )}
        >
          <div className="flex items-center gap-1 pb-2 border-b">
            <button
              type="button"
              onClick={() => onChange(values, false)}
              className={cn(
                "flex-1 rounded px-2 py-1 text-xs",
                !exclude ? "bg-primary text-primary-foreground" : "hover:bg-accent",
              )}
            >
              Include
            </button>
            <button
              type="button"
              onClick={() => onChange(values, true)}
              className={cn(
                "flex-1 rounded px-2 py-1 text-xs",
                exclude ? "bg-destructive text-destructive-foreground" : "hover:bg-accent",
              )}
            >
              Exclude
            </button>
          </div>

          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search…"
            className="mt-2 mb-1 h-7 w-full rounded border border-input bg-background px-2 text-xs"
          />

          <ul className="max-h-64 overflow-auto py-1">
            {filtered.length === 0 && (
              <li className="px-2 py-1 text-xs text-muted-foreground">No matches</li>
            )}
            {filtered.map((o, idx) => {
              const showHeader = o.group && (idx === 0 || filtered[idx - 1]?.group !== o.group);
              const checked = selected.has(o.value);
              return (
                <div key={o.value}>
                  {showHeader && (
                    <li className="px-2 pt-2 pb-1 text-[10px] uppercase tracking-wide text-muted-foreground">
                      {o.group}
                    </li>
                  )}
                  <li>
                    <button
                      type="button"
                      onClick={() => toggle(o.value)}
                      className="flex w-full items-center gap-2 rounded px-2 py-1 text-left text-xs hover:bg-accent"
                    >
                      <span
                        className={cn(
                          "inline-flex h-4 w-4 items-center justify-center rounded border",
                          checked
                            ? "border-primary bg-primary text-primary-foreground"
                            : "border-input",
                        )}
                      >
                        {checked && <Check className="h-3 w-3" />}
                      </span>
                      <span className="truncate flex-1">{o.label}</span>
                    </button>
                  </li>
                </div>
              );
            })}
          </ul>

          {values.length > 0 && (
            <div className="flex items-center justify-between pt-2 border-t text-xs">
              <span className="text-muted-foreground">
                {values.length} {exclude ? "excluded" : "selected"}
              </span>
              <button
                type="button"
                onClick={clearAll}
                className="text-muted-foreground hover:text-foreground"
              >
                Clear
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
